import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MAILTO_LOG } from "@/constants/testIds";
import { toast, confirmToast } from "@/lib/notify";
import {
  History,
  RefreshCw,
  Trash2,
  Eraser,
} from "lucide-react";

const TEMPLATE_KEY_LABEL = {
  reminder_48h: "Recordatorio 48h",
  request_decision: "Decisión solicitud",
  request_new: "Nueva solicitud",
  other: "Otro",
};

function formatLogTimestamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

export default function MailtoLogSection() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "administrador";

  const [logs, setLogs] = useState([]);
  const [filterTpl, setFilterTpl] = useState("");
  const [since, setSince] = useState("");
  const [includeTests, setIncludeTests] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (filterTpl) params.set("template_key", filterTpl);
      if (since) params.set("since", `${since}T00:00:00+00:00`);
      if (includeTests) params.set("include_tests", "true");
      params.set("limit", "200");
      const { data } = await api.get(`/mailto-logs?${params.toString()}`);
      setLogs(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando historial");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    queueMicrotask(load);
  }, [filterTpl, since, includeTests]);

  const removeRow = async (id) => {
    if (!isAdmin) return;
    const ok = await confirmToast("¿Eliminar este registro del historial?", {
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    try {
      await api.delete(`/mailto-logs/${id}`);
      setLogs((prev) => prev.filter((r) => r.id !== id));
      toast.success("Registro eliminado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  const purgeTests = async () => {
    if (!isAdmin) return;
    const ok = await confirmToast(
      "¿Eliminar todos los envíos de prueba?",
      {
        description: "Esta acción no se puede deshacer.",
        confirmLabel: "Eliminar",
      },
    );
    if (!ok) return;
    try {
      const { data } = await api.delete("/mailto-logs/tests");
      await load();
      toast.success(`${data.deleted} envío(s) de prueba eliminado(s).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar pruebas");
    }
  };

  return (
    <section
      data-testid={MAILTO_LOG.section}
      className="mt-10 border border-[#d9cfb8] bg-white p-5 shadow-sm"
    >
      <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#1c1a14]">
            <History size={16} className="text-[#7a5b0f]" />
            <h3 className="font-serif text-base">Historial de envíos (mailto:)</h3>
          </div>
          <p className="text-[11px] text-[#5d5849] mt-0.5">
            Registro de cada email manual que el sistema preparó.
            {isAdmin
              ? " Ves los envíos de todos los usuarios."
              : " Solo ves tus propios envíos."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              type="button"
              data-testid="mailto-log-purge-tests"
              onClick={purgeTests}
              title="Eliminar todos los envíos de prueba"
              className="inline-flex items-center gap-1 rounded border border-amber-400 bg-amber-50 px-3 py-1.5 text-[11px] uppercase tracking-widest text-amber-900 hover:bg-amber-100"
            >
              <Eraser size={12} /> Limpiar pruebas
            </button>
          )}
          <button
            type="button"
            data-testid={MAILTO_LOG.refresh}
            onClick={load}
            className="inline-flex items-center gap-1 rounded border border-[#d9cfb8] px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#fbf7ee]"
          >
            <RefreshCw size={12} /> Actualizar
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-3">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
            Filtrar por tipo
          </label>
          <select
            data-testid={MAILTO_LOG.filterTemplate}
            value={filterTpl}
            onChange={(e) => setFilterTpl(e.target.value)}
            className="border border-[#d9cfb8] bg-white px-2 py-1 text-[12px] rounded"
          >
            <option value="">Todos</option>
            <option value="reminder_48h">Recordatorio 48h</option>
            <option value="request_decision">Decisión solicitud</option>
            <option value="request_new">Nueva solicitud</option>
            <option value="other">Otro</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
            Desde
          </label>
          <input
            data-testid={MAILTO_LOG.filterSince}
            type="date"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="border border-[#d9cfb8] bg-white px-2 py-1 text-[12px] rounded"
          />
        </div>
        <label className="flex items-end gap-2 pb-1 cursor-pointer select-none">
          <input
            data-testid="mailto-log-include-tests"
            type="checkbox"
            checked={includeTests}
            onChange={(e) => setIncludeTests(e.target.checked)}
            className="h-4 w-4 accent-[#c8a04a]"
          />
          <span className="text-[12px] text-[#1c1a14]">
            Mostrar correos de prueba
          </span>
        </label>
      </div>

      <div className="overflow-x-auto border border-[#e8e1cd] rounded">
        <table
          data-testid={MAILTO_LOG.table}
          className="min-w-full text-[12px]"
        >
          <thead className="bg-[#fbf7ee] text-[#5d5849]">
            <tr className="text-left">
              <th className="px-3 py-2 font-medium">Fecha</th>
              <th className="px-3 py-2 font-medium">Tipo</th>
              <th className="px-3 py-2 font-medium">Destinatario</th>
              <th className="px-3 py-2 font-medium">Asunto / Contexto</th>
              {isAdmin && (
                <>
                  <th className="px-3 py-2 font-medium">Enviado por</th>
                  <th className="px-3 py-2 font-medium w-10"></th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={isAdmin ? 6 : 4} className="px-3 py-4 text-[#8b7e5e] italic">
                  Cargando…
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr data-testid={MAILTO_LOG.emptyRow}>
                <td
                  colSpan={isAdmin ? 6 : 4}
                  className="px-3 py-4 text-[#8b7e5e] italic text-center"
                >
                  Sin registros para los filtros seleccionados.
                </td>
              </tr>
            ) : (
              logs.map((r) => (
                <tr
                  key={r.id}
                  data-testid={MAILTO_LOG.row(r.id)}
                  className="border-t border-[#e8e1cd] hover:bg-[#fbf7ee]"
                >
                  <td className="px-3 py-2 whitespace-nowrap text-[#1c1a14]">
                    {formatLogTimestamp(r.created_at)}
                  </td>
                  <td className="px-3 py-2">
                    <span className="inline-block px-2 py-0.5 rounded border border-[#d9cfb8] bg-[#fbf7ee] text-[10px] uppercase tracking-wider">
                      {TEMPLATE_KEY_LABEL[r.template_key] || r.template_key}
                    </span>
                  </td>
                  <td className="px-3 py-2 break-all text-[#1c1a14]">
                    {r.recipient}
                  </td>
                  <td className="px-3 py-2 text-[#1c1a14]">
                    <div className="font-medium flex items-center gap-2">
                      <span>{r.subject || "—"}</span>
                      {r.context_label?.startsWith("Prueba de plantilla") && (
                        <span
                          data-testid={`mailto-log-test-badge-${r.id}`}
                          className="inline-block px-1.5 py-0.5 rounded border border-amber-400 bg-amber-50 text-[9px] uppercase tracking-widest text-amber-800"
                          title="Envío de prueba (no cuenta en estadísticas pastorales)"
                        >
                          Prueba
                        </span>
                      )}
                    </div>
                    {r.context_label && (
                      <div className="text-[10px] text-[#8b7e5e]">
                        {r.context_label}
                      </div>
                    )}
                  </td>
                  {isAdmin && (
                    <>
                      <td className="px-3 py-2 text-[#5d5849] whitespace-nowrap">
                        {r.created_by}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          data-testid={MAILTO_LOG.deleteButton(r.id)}
                          onClick={() => removeRow(r.id)}
                          className="text-[#8b7e5e] hover:text-red-700"
                          title="Eliminar registro"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-[#8b7e5e] mt-2">
        {logs.length} registro(s) mostrado(s).
      </div>
    </section>
  );
}
